import { useMemo, type CSSProperties, type MouseEvent, type ReactNode } from 'react';
import { SEO } from '@/components/SEO';
import { hqFromSatelliteUrl } from '@/hq/urls';
import { requireSatellite } from '../config';
import { trackSatelliteAction } from '../analytics';

export const nunito = {
  fontFamily:
    "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
};

function isModifiedClick(event: MouseEvent<HTMLAnchorElement>): boolean {
  return event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;
}

export function RateLink({
  to,
  children,
  className,
  style,
  onClick,
}: {
  to: string;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  onClick?: () => void;
}) {
  function handleClick(event: MouseEvent<HTMLAnchorElement>) {
    onClick?.();
    if (!to.startsWith('/') || isModifiedClick(event)) return;
    event.preventDefault();
    if (window.location.pathname !== to) {
      window.history.pushState({}, '', `${to}${window.location.search}`);
      window.dispatchEvent(new PopStateEvent('popstate'));
      window.scrollTo(0, 0);
    }
  }
  return (
    <a href={to} className={className} style={style} onClick={handleClick}>
      {children}
    </a>
  );
}

export function RateShell({
  apex,
  name,
  title,
  description,
  path,
  harborHome,
  cross,
  ogImage,
  noindex,
  children,
}: {
  apex: string;
  name: string;
  title: string;
  description: string;
  path: string;
  harborHome: string;
  cross?: { href: string; label: string };
  ogImage?: string;
  noindex?: boolean;
  children: ReactNode;
}) {
  const config = requireSatellite(apex);
  const jsonLd = useMemo(
    () =>
      path === '/'
        ? [
            {
              '@context': 'https://schema.org',
              '@type': 'WebApplication',
              name: config.name,
              url: `https://${apex}/`,
              applicationCategory: 'LifestyleApplication',
              operatingSystem: 'Web',
              description: config.description,
              publisher: {
                '@type': 'Organization',
                name: 'Harbor',
                url: 'https://harborcoparent.com',
              },
              offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
            },
          ]
        : undefined,
    [apex, config.description, config.name, path],
  );

  return (
    <div className='min-h-screen bg-[#f3f4f6] text-gray-900'>
      <SEO
        title={title}
        description={description}
        path={path}
        baseUrl={`https://${apex}`}
        siteName={name}
        ogImage={ogImage}
        noindex={noindex}
        jsonLd={jsonLd}
      />
      <header className='px-4 pt-6'>
        <div className='mx-auto flex max-w-lg items-center justify-between'>
          <RateLink to='/' className='text-lg font-bold tracking-tight' style={nunito}>
            {name}
          </RateLink>
          <a
            href={harborHome}
            className='text-sm text-gray-500 hover:text-primary'
            style={nunito}
            onClick={() =>
              trackSatelliteAction('Satellite Harbor CTA Pressed', config, { location: 'header' })
            }
          >
            Built by Harbor
          </a>
        </div>
      </header>
      <main className='mx-auto max-w-lg px-4 py-6'>
        <div className='rounded-3xl bg-white px-5 py-8 shadow-sm sm:px-8'>{children}</div>
      </main>
      <footer className='px-4 pb-10'>
        <div className='mx-auto max-w-lg space-y-2 text-center text-xs text-gray-500' style={nunito}>
          <p>
            your votes are private and used only in aggregate · {name} ·{' '}
            <a
              href={harborHome}
              className='underline-offset-2 hover:underline'
              onClick={() =>
                trackSatelliteAction('Satellite Harbor CTA Pressed', config, { location: 'footer' })
              }
            >
              Built by Harbor
            </a>
          </p>
          {cross ? (
            <p>
              <a href={cross.href} className='text-primary underline-offset-2 hover:underline'>
                {cross.label}
              </a>
            </p>
          ) : null}
          <p>
            More coparent tools at{' '}
            <a href={hqFromSatelliteUrl(apex)} className='underline-offset-2 hover:underline'>
              Co-parenting HQ
            </a>
            . Educational, not legal advice. No names.
          </p>
        </div>
      </footer>
    </div>
  );
}
